<script setup>
import router from "@/router";
import { useRoute } from "vue-router";
import { onMounted, ref } from "vue";
import Card from "@/components/ui/card/Card.vue";
import HeaderView from "./HeaderView.vue";
import Item from "@/components/ui/item/Item.vue";
import { AlertCircleIcon, PencilIcon } from "lucide-vue-next";
import Input from "@/components/ui/input/Input.vue";
import Alert from "@/components/ui/alert/Alert.vue";
import AlertTitle from "@/components/ui/alert/AlertTitle.vue";
import AlertDescription from "@/components/ui/alert/AlertDescription.vue";
import Button from "@/components/ui/button/Button.vue";

const props = defineProps({
  avatarUrl: {
    type: String,
    required: true,
  },
  nickname: {
    type: String,
    required: true,
  },
  steamId: {
    type: String,
    required: true,
  },
  steamUsername: {
    type: String,
    required: true,
  },
  isPrivate: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['saveName'])

const route = useRoute();
const isFromAuth = route.query.from_auth === "true";

onMounted(() => {
  if (isFromAuth) {
    // Replace the URL to remove the query parameter
    router.replace({ query: {} });
  }
});

// Custom return action for BackButton to handle post-auth navigation
function returnACB() {
  if (isFromAuth) {
    history.go(-2);
  } else {
    router.back();
  }
}

const logoutUrl =
  `https://iprog.koda.re/auth/logout?redirect=` +
  encodeURIComponent(window.location.origin + "#/profile");

const isEditing = ref(false);
const editableNickname = ref('')

function startEditing(){
  editableNickname.value = props.nickname;
  isEditing.value = true;
}

function saveNickname(){
  emit('saveName', editableNickname.value.trim() || props.nickname);
  isEditing.value = false;
}

</script>

<template>
  <Card
    class="grid grid-cols-1 place-content-center w-full w-fit px-10 mb-10"
  >
    <img
      :src="props.avatarUrl"
      alt="User Avatar"
      class="avatar square w-32 rounded-xl place-self-center"
    />
    
    <div v-if="isEditing">
      <h2
        class="flex flex-row items-center gap-2 place-self-center text-primary"
      > 
        <input
          v-model="editableNickname"
          class="border rounded px-2 py-1 text-primary"
          @blur="saveNickname"
          @keyup.enter="saveNickname"
          autofocus
        />
      </h2>
    </div>
    <div v-else>
      <h2
        class="flex flex-row items-center gap-2 place-self-center text-primary"
      >
        {{ props.nickname }}
        <Button variant="ghost" @click="startEditing"
          ><PencilIcon class="w-4 h-4 text-primary"></PencilIcon
        ></Button>
      </h2>
    </div>
    <h2
        class="flex flex-row items-center gap-2 place-self-center text-primary"
      >
        Steam Name: {{ props.steamUsername }}
      </h2>
  </Card>
  <Alert v-if="isPrivate" variant="destructive" class="lg:mb-10 w-full lg:w-lg">
    <AlertCircleIcon></AlertCircleIcon>
    <AlertTitle>Unable to fetch steam games</AlertTitle>
    <AlertDescription>
      Notice: Your steam library privacy status is set to private. Therefore you
      can't use the game interest groups on each LAN.
    </AlertDescription>
  </Alert>
  <a class="w-80" :href="logoutUrl"
    ><Button class="w-full" variant="destructive">Logout</Button></a
  >
  <div class="h-12"></div>
</template>
